import { useMemo } from "react";
import {
  GameCategoryInfo,
  GameCategoryType,
} from "@/features/common/gameCategory/gameCategory";
import {
  filterGameCategories,
  defaultGameCategories,
  sortGameCategories,
} from "@/features/common/gameCategory/gameCategoryUtils";
import {
  filterCategoriesByGameList,
  selectVisibleCategories,
} from "@/features/common/gameCategory/gameCategoryVisibility";
import { useGlobalState } from "@/store/commonStore";
import { useCasino } from "@/store/home";
import React from "react";

/**
 * 获取游戏分类配置的Hook
 */
export function useGameCategories() {
  // 获取用户登录状态
  const { isLogin } = useGlobalState();

  // 复用 useCasino 的 lobby 数据，避免重复请求
  const {
    homeData: lobbyData,
    bigWinsData,
    playedPreviewData,
    isLoading,
    error,
    mutate,
  } = useCasino();

  const sortedCategories = useMemo(
    () => sortGameCategories(defaultGameCategories),
    []
  );

  // 获取所有启用的分类
  const allCategories = useMemo(() => {
    return filterGameCategories(sortedCategories, {
      isLogin,
      includeAuth: true,
    });
  }, [isLogin, sortedCategories]);

  const categoriesWithAvailableGames = useMemo(() => {
    return filterCategoriesByGameList(sortedCategories, lobbyData);
  }, [lobbyData, sortedCategories]);

  // 获取首页标签页分类（排除首页本身）
  const homeTabCategories = useMemo(() => {
    return selectVisibleCategories(categoriesWithAvailableGames, {
      isLogin,
      excludeFavorite: true,
    });
  }, [categoriesWithAvailableGames, isLogin]);

  // 获取Casino页面显示的分类
  const casinoCategories = useMemo(() => {
    return filterCategoriesByGameList(
      filterGameCategories(sortedCategories, {
        isLogin,
        includeAuth: false, // Casino页面不显示需要认证的分类
      }).filter((cat) => cat.dataKey),
      lobbyData
    );
  }, [isLogin, lobbyData, sortedCategories]);

  // 获取侧边栏菜单分类
  const menuCategories = useMemo(() => {
    return selectVisibleCategories(categoriesWithAvailableGames, {
      isLogin,
      omitFirst: true,
    });
  }, [categoriesWithAvailableGames, isLogin]);

  return {
    // 数据状态
    isLoading,
    error,
    mutate,

    // 原始数据
    lobbyData,
    bigWinsData,
    playedPreviewData,

    // 处理后的分类数据
    allCategories,
    homeTabCategories,
    casinoCategories,
    menuCategories,

    // 工具方法
    getCategory: (key: string) => allCategories.find((cat) => cat.key === key),
    getCategoriesByType: (type: string) =>
      allCategories.filter((cat) => cat.type === type),
  };
}

/**
 * 获取特定用途的游戏分类
 */
export function useFilteredGameCategories(
  options: {
    includeAuth?: boolean;
    mobileOnly?: boolean;
    excludeTypes?: GameCategoryType[];
    includeTypes?: GameCategoryType[];
  } = {}
) {
  const { allCategories, isLoading, error } = useGameCategories();
  const { isLogin } = useGlobalState();

  const filteredCategories = useMemo(() => {
    return filterGameCategories(allCategories, {
      ...options,
      isLogin,
    });
  }, [allCategories, options, isLogin]);

  return {
    categories: filteredCategories,
    isLoading,
    error,
  };
}
